import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Amount } from '@welldev-group/react-comp-lib';
import Button from '@welldev-group/rcl-button';
import Link from '@welldev-group/rcl-link';
import { StringParam, useQueryParam } from 'use-query-params';
import withQueryParamsProvider from '../../../../shared/components/withQueryParamsProvider';
import httpClient from '../../../../shared/libraries/httpClient';
import routes from '../../../../shared/routes';
import DateTime from '../../DateTime';
import SubscriptionPageLayout from './SubscriptionPageLayout';
import NewPlanDetails from './NewPlanDetails';
import SubscriptionDivider from './SubscriptionDivider';
import ActiveUsersDetails from './ActiveUsersDetails';
import CostsList from './CostsList';
import NoteList from './NoteList';
import PaymentSection from './PaymentSection';
import SubscriptionWaitingModal from './SubscriptionWaitingModal';
import SubscriptionFeatureDetailsModal from './SubscriptionFeatureDetailsModal';
import SubscriptionErrorModal from './SubscriptionErrorModal';

const MONTH_INDICATOR_KEY = 'monthly';
const YEAR_INDICATOR_KEY = 'yearly';
const API_ERROR_FOR_IMPOSSIBLE_UPGRADE = 'would_be_downgrade';

const SubscriptionUpgrade = ({
  affiliateId,
  plan,
  urls,
  wouldRejectOtherPeriod,
}) => {
  const [identifier] = useQueryParam('identifier', StringParam);
  const [period, setPeriod] = useQueryParam('period', StringParam);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [upgradedFeatureDetails, setUpgradedFeatureDetails] = useState({});
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [showFeatureDetailsModal, setShowFeatureDetailsModal] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [errorRedirectUrl, setErrorRedirectUrl] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const isYearly = period === YEAR_INDICATOR_KEY;
  const costItems = [
    {
      id: 0,
      title: subscriptionDetails?.totalFullPeriod.title,
      hint: '',
      amount: subscriptionDetails?.totalFullPeriod.amount || '',
      highlighted: false,
      showItem: true,
    },
    {
      id: 1,
      title: subscriptionDetails?.oldPlanPrepayment?.title,
      hint: subscriptionDetails?.oldPlanPrepayment?.hint,
      amount: subscriptionDetails?.oldPlanPrepayment?.amount || '',
      highlighted: false,
      showItem: subscriptionDetails?.oldPlanPrepayment,
    },
    {
      id: 2,
      title: subscriptionDetails?.newPlanProrata.title,
      hint: subscriptionDetails?.newPlanProrata.hint,
      amount: subscriptionDetails?.newPlanProrata.amount || '',
      highlighted: false,
      showItem: true,
    },
    {
      id: 3,
      title: subscriptionDetails?.total.title,
      hint: '',
      amount: subscriptionDetails?.total.amount || '',
      highlighted: true,
      showItem: true,
    },
  ];
  const noteItems = [
    {
      id: 0,
      note:
        subscriptionDetails?.upgradeChargedBy === 'now'
          ? I18n.t('components.subscription.upfront_payment_text')
          : I18n.t('components.subscription.regular_payment_text', {
              from: DateTime({
                dateTime: subscriptionDetails?.upgradeChargedBy,
                format: 'short',
                returnStringOnly: true,
              }),
            }),
    },
    {
      id: 1,
      note: I18n.t('components.subscription.payment_details_html', {
        fromDate: DateTime({
          dateTime: subscriptionDetails?.nextBillingDate,
          format: 'short',
          returnStringOnly: true,
        }),
        chargeAmount: Amount({
          minFractionDigit: 2,
          currency: subscriptionDetails?.currency || 'USD', // We have to put a default valid currency here. That's why 'USD' is used. Otherwise, it'll throw error.
          value: subscriptionDetails?.totalFullPeriod.amount || '',
          currencyDisplay: 'code',
          returnStringOnly: true,
        }),
        period: isYearly ? 'year' : 'month',
      }),
    },
  ];

  const fetchSubscriptionDetails = async params => {
    try {
      const { data } = await httpClient.get(
        routes.billing.subscriptions.upgrade.fetch(params),
      );

      setSubscriptionDetails(data);
    } catch ({ response }) {
      if (
        response.status === 422 &&
        response.data.error === API_ERROR_FOR_IMPOSSIBLE_UPGRADE
      ) {
        setErrorRedirectUrl(urls.plans);
        setErrorText(
          I18n.t(
            'components.pricing.plans.subscription_mode_switch_disable_text',
            {
              planName: I18n.t(plan.name, {
                scope: 'components.plans',
              }),
              planPeriod: plan.period,
            },
          ),
        );
      } else {
        setErrorRedirectUrl(null);
        setErrorText(response.data.error);
      }
      setShowErrorModal(true);
    }
  };

  const changePlan = async () => {
    setShowWaitingModal(true);

    httpClient
      .post(
        routes.billing.subscriptions.upgrade.changePlan({
          affiliate_id: affiliateId,
          id: subscriptionDetails?.newPlan.id,
        }),
      )
      .then(response => {
        if (response.status === 200) {
          setUpgradedFeatureDetails(response.data.featureChanges);
          setShowWaitingModal(false);
          setShowFeatureDetailsModal(true);
        }
      })
      .catch(({ response }) => {
        if (
          response.status === 422 &&
          response.data.error === API_ERROR_FOR_IMPOSSIBLE_UPGRADE
        ) {
          setErrorRedirectUrl(urls.plans);
          setErrorText(
            I18n.t(
              'components.pricing.plans.subscription_mode_switch_disable_text',
              {
                planName: I18n.t(plan.name, {
                  scope: 'components.plans',
                }),
                planPeriod: plan.period,
              },
            ),
          );
        } else {
          setErrorText(response.data.error);
        }

        setShowErrorModal(true);
        setShowWaitingModal(false);
      });
  };

  useEffect(() => {
    fetchSubscriptionDetails({
      affiliate_id: affiliateId,
      identifier,
      period,
    });
  }, [affiliateId, identifier, period]);

  return (
    <>
      <SubscriptionWaitingModal
        isVisible={showWaitingModal}
        waitingText={I18n.t('components.subscription.upgrade_waiting_text')}
      />
      <SubscriptionErrorModal
        isVisible={showErrorModal}
        errorText={errorText}
        errorRedirectUrl={errorRedirectUrl}
        onClose={() => setShowErrorModal(false)}
      />
      <SubscriptionFeatureDetailsModal
        isVisible={showFeatureDetailsModal}
        greetingText={I18n.t(
          'components.subscription.subscription_feature_details_modal.upgrade.greeting_text',
        )}
        successText={
          subscriptionDetails
            ? I18n.t(
                'components.subscription.subscription_feature_details_modal.upgrade.success_text.new_plan_details',
                {
                  planName: I18n.t(subscriptionDetails?.newPlan.name, {
                    scope: 'components.plans',
                  }),
                  planPeriod: subscriptionDetails?.newPlan.period,
                },
              )
            : null
        }
        upgradedFeatureDetails={upgradedFeatureDetails}
        phoneSupportQuantity={subscriptionDetails?.newPlan.phone}
        redirectTo={urls.billing}
      />
      <SubscriptionPageLayout
        title={I18n.t('components.subscription.upgrade_page_title')}
        footer={
          <>
            <Button
              label={I18n.t(
                'components.subscription.details_page.footer.upgrade',
              )}
              type="primary"
              size="small"
              onClick={changePlan}
            />
            <Link
              type="button"
              size="small"
              modifier="default"
              href={urls.plans}
            >
              {I18n.t('components.subscription.details_page.footer.cancel')}
            </Link>
          </>
        }
      >
        <NewPlanDetails
          className="subscription-page__invoice-plan subscription-page__invoice-plan--success-light"
          plan={subscriptionDetails?.newPlan}
          period={period}
          onSubscriptionPeriodChange={() =>
            setPeriod(isYearly ? MONTH_INDICATOR_KEY : YEAR_INDICATOR_KEY)
          }
          disableToggle={wouldRejectOtherPeriod}
          toggleDisabilityReasonText={I18n.t(
            'components.pricing.plans.subscription_mode_switch_disable_text',
            {
              planName: I18n.t(subscriptionDetails?.newPlan.name, {
                scope: 'components.plans',
              }),
              planPeriod:
                subscriptionDetails?.newPlan.period === YEAR_INDICATOR_KEY
                  ? MONTH_INDICATOR_KEY
                  : YEAR_INDICATOR_KEY,
            },
          )}
        />
        <SubscriptionDivider className="subscription-page__invoice-divider" />
        <ActiveUsersDetails
          className="subscription-page__invoice-active-users"
          activeUsers={subscriptionDetails?.activeUsers}
        />
        <CostsList
          className="subscription-page__invoice-costs"
          items={costItems}
          currency={subscriptionDetails?.currency}
        />
        <NoteList
          className="subscription-page__invoice-note"
          items={noteItems}
          termsOfUseUrl={urls.termsOfUse}
        />
        <PaymentSection
          className="subscription-page__invoice-payment"
          currentBalance={subscriptionDetails?.payment.balance}
          workflow={subscriptionDetails?.payment.workflow}
          currency={subscriptionDetails?.currency}
          paymentAlert={subscriptionDetails?.payment.alert}
          creditCard={subscriptionDetails?.payment.creditCard}
        />
      </SubscriptionPageLayout>
    </>
  );
};

SubscriptionUpgrade.defaultProps = {
  wouldRejectOtherPeriod: true,
};

SubscriptionUpgrade.propTypes = {
  affiliateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  plan: PropTypes.shape({
    name: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
  }).isRequired,
  urls: PropTypes.shape({
    termsOfUse: PropTypes.string.isRequired,
    billing: PropTypes.string.isRequired,
    plans: PropTypes.string.isRequired,
  }).isRequired,
  wouldRejectOtherPeriod: PropTypes.bool,
};

export default withQueryParamsProvider(SubscriptionUpgrade);
